// src/pages/public/StaffInviteClaimPage.js
//
// Public (NO AUTH) claim link for league-provisioned coach/official SMS
// invites: https://app.polyp.me/join/{inviteId}.{secret}. Also the iOS
// universal-link target — see public/.well-known/apple-app-site-association
// and the Polyp.entitlements applinks:app.polyp.me entry.
//
// Looks the invite up (polyp-staff-invite-claim, unauthenticated), lets the
// invitee set their own name/email/password, then signs the browser in via
// the returned Firebase custom token — same completion shape as
// LoginPage's signInWithCustomToken(customToken) call.

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { signInWithCustomToken } from "firebase/auth";
import { auth } from "../../firebase";
import ApiService from "../../services/api.service";
import "./StaffInviteClaimPage.css";

function errorMessage(err) {
  const msg = err?.message || "";
  if (msg.includes("inviteInvalid")) return "This invite link is invalid, expired, or already used.";
  if (msg.includes("emailInvalid")) return "Enter a valid email address.";
  if (msg.includes("passwordTooShort")) return "Password must be at least 8 characters.";
  if (msg.includes("passwordTooWeak")) return "Password needs an uppercase letter, a lowercase letter, and a number.";
  if (msg.includes("emailInUse")) return "That email is already used by a different kind of Polyp account.";
  if (msg.includes("phoneAlreadyClaimed")) return "This phone number is already linked to another Polyp account.";
  return "Something went wrong. Please try again.";
}

export default function StaffInviteClaimPage() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [lookupError, setLookupError] = useState(null);
  const [invite, setInvite] = useState(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await ApiService.lookupStaffInvite(token);
        if (cancelled) return;
        setInvite(resp);
        setFirstName(resp.firstName || "");
        setLastName(resp.lastName || "");
      } catch (err) {
        if (!cancelled) setLookupError(errorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const passwordChecks = {
    length: password.length >= 8,
    upper: /[A-Z]/.test(password),
    lower: /[a-z]/.test(password),
    number: /\d/.test(password),
  };
  const passwordValid = Object.values(passwordChecks).every(Boolean);
  const canSubmit = firstName.trim() && lastName.trim() && email.includes("@") && passwordValid && !submitting;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const resp = await ApiService.claimStaffInvite({
        token,
        email: email.trim().toLowerCase(),
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      await signInWithCustomToken(auth, resp.customToken);
      // RoleContext caches accountType/role under this key — clear it so a
      // browser that was previously signed in (e.g. a shared device) can't
      // serve a stale role for the new session.
      try { localStorage.removeItem("polyp_role"); } catch {}
      navigate(resp.accountType === "official" ? "/official" : "/coach", { replace: true });
    } catch (err) {
      setSubmitError(errorMessage(err));
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="staff-invite-bg">
        <div className="staff-invite-card">
          <p className="staff-invite-loading">Loading invite…</p>
        </div>
      </div>
    );
  }

  if (lookupError || !invite) {
    return (
      <div className="staff-invite-bg">
        <div className="staff-invite-card">
          <a href="https://polyp.me" className="staff-invite-brand"><em>Polyp.</em></a>
          <h1 className="staff-invite-title">Invite not found</h1>
          <p className="staff-invite-subtitle">{lookupError || "This invite link is invalid or has expired."}</p>
        </div>
      </div>
    );
  }

  const roleLabel = invite.accountType === "official" ? "game official" : "coach";
  const headline = invite.teamName
    ? `${invite.leagueName} invited you to coach ${invite.teamName}`
    : `${invite.leagueName} invited you as a ${roleLabel}`;

  return (
    <div className="staff-invite-bg">
      <div className="staff-invite-card">
        <a href="https://polyp.me" className="staff-invite-brand"><em>Polyp.</em></a>
        <h1 className="staff-invite-title">{headline}</h1>
        {invite.maskedPhone && <p className="staff-invite-subtitle">Invite sent to {invite.maskedPhone}</p>}

        {submitError && <div className="alert error">{submitError}</div>}

        <form onSubmit={handleSubmit} className="staff-invite-form">
          <div className="staff-invite-row">
            <input
              className="staff-invite-input"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={submitting}
            />
            <input
              className="staff-invite-input"
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={submitting}
            />
          </div>

          <input
            className="staff-invite-input"
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            disabled={submitting}
          />

          <div className="staff-invite-field-password">
            <input
              className="staff-invite-input"
              type={showPassword ? "text" : "password"}
              placeholder="Create a password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              disabled={submitting}
            />
            {password && (
              <button
                type="button"
                className="staff-invite-eye-toggle"
                onClick={() => setShowPassword((v) => !v)}
                tabIndex={-1}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            )}
          </div>

          {password && (
            <ul className="staff-invite-password-checklist">
              <li className={passwordChecks.length ? "met" : ""}>8+ characters</li>
              <li className={passwordChecks.upper ? "met" : ""}>Uppercase letter</li>
              <li className={passwordChecks.lower ? "met" : ""}>Lowercase letter</li>
              <li className={passwordChecks.number ? "met" : ""}>Number</li>
            </ul>
          )}

          <button className="staff-invite-submit" disabled={!canSubmit} type="submit">
            {submitting ? "Setting up…" : "Finish Setting Up"}
          </button>
        </form>
      </div>
    </div>
  );
}
